import React from 'react';
import { School, Plus, Trash2 } from 'lucide-react';
import { Row, Col, Input, Select, SectionTitle, FormField, FileInput, Button, Checkbox } from '../../../SharedInputs.jsx';
import { COMPLETED_LEVELS } from '../../../../constants.js';

export function SectionSchoolingQualifications({ value, onChange, errors = [], dispatch }) {
  const updateField = (field, fieldValue) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const updatePriorEducation = (index, field, fieldValue) => {
    dispatch({ 
      type: 'UPDATE_PRIOR_EDU', 
      index, 
      payload: { [field]: fieldValue } 
    });
  };

  const addPriorEducation = () => {
    dispatch({ type: 'ADD_PRIOR_EDU' });
  };

  const removePriorEducation = (index) => {
    if (value.priorEducationList.length > 1) {
      dispatch({ type: 'REMOVE_PRIOR_EDU', index });
    }
  };

  const toggleCompletedLevel = (level) => {
    dispatch({ 
      type: 'TOGGLE_ARRAY_ITEM', 
      section: 'schoolingQualifications',
      field: 'completedLevels',
      value: level 
    });
  };

  const updateTranscripts = (files) => {
    dispatch({ 
      type: 'SET_FILES', 
      section: 'schoolingQualifications', 
      field: 'transcripts', 
      files: Array.from(files) 
    });
  };

  const getFieldError = (fieldName) => {
    return errors.find(error => error.toLowerCase().includes(fieldName.toLowerCase()));
  };

  return (
    <div>
      <SectionTitle icon={School} step={7}>
        Schooling & Qualification Background
      </SectionTitle>

      <div style={{
        padding: 16,
        background: '#f8faff',
        borderRadius: 8,
        border: '1px solid #e0e7ff',
        marginBottom: 24
      }}>
        <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
          Please provide details about your educational background, including secondary school 
          completion and any post-secondary qualifications. This helps us assess your readiness 
          for your chosen course and identify any prerequisite requirements.
        </p>
      </div>

      {/* Secondary Education */}
      <Row>
        <Col span={6}>
          <FormField label="Highest completed school level">
            <Select
              value={value.highestSchoolLevel}
              onChange={(e) => updateField('highestSchoolLevel', e.target.value)}
            >
              <option value="">Select</option>
              <option value="Year 12 or equivalent">Year 12 or equivalent</option>
              <option value="Year 11 or equivalent">Year 11 or equivalent</option>
              <option value="Year 10 or equivalent">Year 10 or equivalent</option>
              <option value="Year 9 or equivalent">Year 9 or equivalent</option>
              <option value="Year 8 or below">Year 8 or below</option>
              <option value="Never attended school">Never attended school</option>
            </Select>
          </FormField>
        </Col>
        <Col span={6}>
          <FormField label="Are you still enrolled in secondary or senior secondary education?">
            <Select
              value={value.stillEnrolledSecondary}
              onChange={(e) => updateField('stillEnrolledSecondary', e.target.value)}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </Select>
          </FormField>
        </Col>
      </Row>

      {/* Current Studies */}
      <Row>
        <Col span={6}>
          <FormField label="Currently enrolled in any studies?">
            <Select
              value={value.currentlyEnrolledAnyStudies}
              onChange={(e) => updateField('currentlyEnrolledAnyStudies', e.target.value)}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </Select>
          </FormField>
        </Col>
      </Row>

      {value.currentlyEnrolledAnyStudies === "Yes" && (
        <div style={{
          padding: 16,
          background: '#f9fafb',
          borderRadius: 8,
          border: '1px solid #e5e7eb',
          marginBottom: 16
        }}>
          <h4 style={{ margin: '0 0 16px', fontSize: 16, color: '#374151' }}>
            Current Study Details
          </h4>
          <Row>
            <Col span={4}>
              <FormField label="Institution Name"
              required
              error={getFieldError("Current institution name")}>
                <Input
                  value={value.currentProviderName}
                  onChange={(e) => updateField('currentProviderName', e.target.value)}
                  placeholder="School/College/University name"
                  required
                  error={getFieldError("Current institution name")}
                />
              </FormField>
            </Col>
            <Col span={4}>
              <FormField label="Course Name"
                required
                error={getFieldError("Current course name")}>
                <Input
                  value={value.currentProviderCourse}
                  onChange={(e) => updateField('currentProviderCourse', e.target.value)}
                  placeholder="Course/Program title"
                  required
                  error={getFieldError("Current course name")}
                />
              </FormField>
            </Col>
            <Col span={4}>
              <FormField label="Institution Contact/Address"
                required
                error={getFieldError("Current institution contact/address")}>
                <Input
                  value={value.currentProviderContact}
                  onChange={(e) => updateField('currentProviderContact', e.target.value)}
                  placeholder="Address or contact details"
                  required
                  error={getFieldError("Current institution contact/address")}
                />
              </FormField>
            </Col>
          </Row>
        </div>
      )}

      {/* English Qualifications */}
      <Row>
        <Col span={12}>
          <FormField label="Have you successfully completed any of the qualifications in English?">
            <Select
              value={value.completedQualificationsInEnglish}
              onChange={(e) => updateField('completedQualificationsInEnglish', e.target.value)}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </Select>
          </FormField>
        </Col>
      </Row>

      {value.completedQualificationsInEnglish === "Yes" && (
        <div style={{
          padding: 16,
          background: '#f0fdf4',
          borderRadius: 8,
          border: '1px solid #bbf7d0',
          marginBottom: 24
        }}>
          <h4 style={{ margin: '0 0 16px', fontSize: 16, color: '#065f46' }}>
            English Language Qualifications Completed
          </h4>
          <p style={{ fontSize: 14, color: '#059669', marginBottom: 16 }}>
            Select all qualification levels you have successfully completed in English:
          </p>
          {/* Error message goes here */}
          {getFieldError("one English qualification") && (
            <p style={{ color: 'red', fontSize: 13, marginBottom: 8 }}>
              {getFieldError("one English qualification")}
            </p>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 8
          }}>
            {COMPLETED_LEVELS.map(level => (
              <Checkbox
                key={level}
                checked={value.completedLevels.includes(level)}
                onChange={() => toggleCompletedLevel(level)}
              >
                {level}
              </Checkbox>
            ))}
          </div>
        </div>
      )}

      {/* Prior Education List */}
      <div style={{ marginTop: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, color: '#374151' }}>
            Prior Education History
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={addPriorEducation}
            icon={Plus}
          >
            Add Institution
          </Button>
        </div>

        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
          List all educational institutions attended, starting with the most recent. 
          Include secondary schools, colleges, universities, and vocational institutions.
        </p>

        {value.priorEducationList.map((education, index) => (
          <div
            key={index}
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: 20,
              marginBottom: 16,
              background: index === 0 ? '#fafafa' : '#fff'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h4 style={{ margin: 0, fontSize: 16, color: '#374151' }}>
                Institution {index + 1} {index === 0 && '(Most Recent)'}
              </h4>
              {index > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removePriorEducation(index)}
                  icon={Trash2}
                  style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                >
                  Remove
                </Button>
              )}
            </div>

            <Row>
              <Col span={6}>
                <FormField 
                  label="Institution Name"
                  required
                  error={getFieldError(`prior education ${index + 1}: institution`)}
                >
                  <Input
                    value={education.institution}
                    onChange={(e) => updatePriorEducation(index, 'institution', e.target.value)}
                    placeholder="School, college, or university name"
                    required
                    error={getFieldError(`prior education ${index + 1}: institution`)}
                  />
                </FormField>
              </Col>
              <Col span={6}>
                <FormField 
                  label="Qualification/Program"
                  required
                  error={getFieldError(`prior education ${index + 1}: qualification`)}
                >
                  <Input
                    value={education.qualification}
                    onChange={(e) => updatePriorEducation(index, 'qualification', e.target.value)}
                    placeholder="e.g. Bachelor of Science, Year 12, Diploma"
                    required
                    error={getFieldError(`prior education ${index + 1}: qualification`)}
                  />
                </FormField>
              </Col>
            </Row>

            <Row>
              <Col span={6}>
                <FormField 
                  label="Location (City, Country)"
                  required
                  error={getFieldError(`prior education ${index + 1}: location`)}
                >
                  <Input
                    value={education.location}
                    onChange={(e) => updatePriorEducation(index, 'location', e.target.value)}
                    placeholder="e.g. Sydney, Australia"
                    required
                    error={getFieldError(`prior education ${index + 1}: location`)}
                  />
                </FormField>
              </Col>
              <Col span={3}>
                <FormField 
                  label="Year Completed"
                  required
                  error={getFieldError(`prior education ${index + 1}: year`)}
                >
                  <Input
                    type="number"
                    value={education.yearCompleted}
                    onChange={(e) => updatePriorEducation(index, 'yearCompleted', e.target.value)}
                    placeholder="YYYY"
                    min="1950"
                    max={new Date().getFullYear() + 5}
                    required
                    error={getFieldError(`prior education ${index + 1}: year`)}
                  />
                </FormField>
              </Col>
              <Col span={3}>
                <FormField label="Status"
                  required
                  error={getFieldError(`prior education ${index + 1}: status`)}>
                  <Select
                    value={education.status || 'Completed'}
                    onChange={(e) => updatePriorEducation(index, 'status', e.target.value)}
                    required
                    error={getFieldError(`prior education ${index + 1}: status`)}
                  >
                    <option value="Completed">Completed</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Incomplete">Incomplete</option>
                  </Select>
                </FormField>
              </Col>
            </Row>
          </div>
        ))}
      </div>

      {/* Document Upload */}
      <div style={{ marginTop: 32 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#374151' }}>
          Academic Transcripts & Certificates
        </h3>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
          Upload certified copies of your academic transcripts, certificates, and diplomas. 
          All documents should be in English or include certified English translations.
        </p>

        <FormField required
          error={getFieldError("transcript")}>
          <FileInput
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={updateTranscripts}
            value={value.transcripts}
            placeholder="Upload academic documents (PDF, images, or Word documents)"
            maxSize={15 * 1024 * 1024} // 15MB
            required
            error={getFieldError("transcript")}
          />
        </FormField>

        <div style={{
          marginTop: 16,
          padding: 12,
          background: '#fffbeb',
          borderRadius: 8,
          border: '1px solid #fed7aa'
        }}>
          <p style={{ fontSize: 13, color: '#b45309', margin: 0, lineHeight: 1.4 }}>
            <strong>Document Requirements:</strong> All academic documents must be certified copies 
            or official transcripts sent directly from the institution. Non-English documents 
            require certified translations. Scan quality should be clear and legible.
          </p>
        </div>
      </div>
    </div>
  );
}